/**
 * @fileoverview Module de gestion de l'interface utilisateur (UI) et du DOM.
 * S'occupe exclusivement du rendu visuel (cartes, statistiques) et des interactions
 * dynamiques (sidebar, menus déroulants). Ne contient aucune logique métier ni accès aux données.
 */

import { formatBytes, formatDate } from './utils.js';
import { formatLoanInfo } from './loan.js';

const _loader = document.getElementById('search-loader');
const _loaderStatus = document.getElementById('loader-status');
const _notification = document.getElementById('notification');
const _notificationIcon = document.getElementById('notification-icon');
const _notificationMessage = document.getElementById('notification-message');
const _notificationClose = document.getElementById('notification-close');

let _notificationTimeout = null;

/**
 * Annule le timeout d'auto-fermeture de la notification s'il existe.
 * @private
 */
function _clearNotificationTimeout() {
    if (_notificationTimeout !== null) {
        clearTimeout(_notificationTimeout);
        _notificationTimeout = null;
    }
}

// Fermeture du notification au clic sur la croix — branché une seule fois ici
_notificationClose.addEventListener('click', () => {
    _clearNotificationTimeout();
    _notification.classList.add('is-hidden');
});

/**
 * Affiche l'overlay de chargement et bloque l'interface.
 * @param {string} status - Le texte de la source en cours d'interrogation.
 */
export function showLoader(status = '') {
    _loaderStatus.textContent = status;
    _loader.classList.remove('is-hidden');
}

/**
 * Met à jour le texte de statut pendant que le loader est affiché.
 * @param {string} status
 */
export function updateLoaderStatus(status) {
    _loaderStatus.textContent = status;
}

/**
 * Masque l'overlay de chargement.
 */
export function hideLoader() {
    _loader.classList.add('is-hidden');
    _loaderStatus.textContent = '';
}

/**
 * Affiche une notification notification.
 * @param {string} message - Le texte à afficher.
 * @param {'success'|'warning'|'error'} type - Le type de notification.
 * @param {number} [autoClose=10000] - Délai en ms avant fermeture auto (0 = pas de fermeture auto).
 */
export function showNotification(message, type = 'success', autoClose = 10000) {
    const icons = { success: '✅', warning: '⚠️', error: '⛔' };

    // Annule tout timeout existant
    _clearNotificationTimeout();

    // Nettoyage des classes précédentes
    _notification.classList.remove('is-success', 'is-warning', 'is-error', 'is-hidden');

    _notificationIcon.textContent = icons[type] ?? '';
    _notificationMessage.textContent = message;
    _notification.classList.add(`is-${type}`);

    if (autoClose > 0) {
        _notificationTimeout = setTimeout(() => _notification.classList.add('is-hidden'), autoClose);
    }
}

/**
 * Rend la collection dans le DOM selon le mode d'affichage demandé.
 * Dispatch vers `_renderGrid` (vignettes) ou `_renderTable` (liste tabulaire).
 *
 * @param {Object[]} comics - Tableau des BD à afficher (déjà filtré et trié).
 * @param {Object} callbacks - Fonctions de rappel pour les actions utilisateur.
 * @param {Function} callbacks.onEdit - Clic sur "Modifier". Reçoit l'objet BD.
 * @param {Function} callbacks.onDelete - Clic sur "Supprimer". Reçoit l'objet BD.
 * @param {Function} callbacks.onToggleLoan - Clic sur "Prêter"/"Rendue". Reçoit l'objet BD.
 * @param {string} [layout='grid'] - 'grid' pour les vignettes, 'list' pour le tableau.
 */
export function renderCards(comics, callbacks, layout = 'grid') {
    if (layout === 'list') {
        _renderTable(comics, callbacks);
    } else {
        _renderGrid(comics, callbacks);
    }
}

/**
 * Rendu en grille de vignettes (mode 'grid').
 * Clone le template `#comic-card-template` pour chaque BD.
 * @private
 */
function _renderGrid(comics, { onEdit, onDelete, onToggleLoan, onDuplicate }) {
    const comicList = document.getElementById('comic-list');
    const template = document.getElementById('comic-card-template');

    if (!comicList || !template) return;

    comicList.innerHTML = '';
    const fragment = document.createDocumentFragment();

    for (const comic of comics) {
        try {
            const clone = template.content.cloneNode(true);
            _fillCard(clone, comic);

            clone.querySelector('.btn-edit').addEventListener('click', (e) => {
                e.stopPropagation();
                onEdit(comic);
            });
            clone.querySelector('.btn-duplicate').addEventListener('click', (e) => {
                e.stopPropagation();
                onDuplicate(comic);
            });
            clone.querySelector('.btn-delete').addEventListener('click', (e) => {
                e.stopPropagation();
                onDelete(comic);
            });
            clone.querySelector('.btn-loan').addEventListener('click', (e) => {
                e.stopPropagation();
                onToggleLoan(comic);
            });

            fragment.appendChild(clone);
        } catch (err) {
            console.error("Impossible d'afficher la BD ID : " + comic.id, err);
        }
    }

    comicList.appendChild(fragment);
}

/**
 * Rendu en tableau (mode 'list').
 * Clone la structure `#comic-table-template`, puis chaque ligne via `#comic-row-template`.
 * @private
 */
function _renderTable(comics, callbacks) {
    const comicList = document.getElementById('comic-list');
    const tableTemplate = document.getElementById('comic-table-template');
    const rowTemplate = document.getElementById('comic-row-template');

    if (!comicList || !tableTemplate || !rowTemplate) return;

    comicList.innerHTML = '';

    const tableClone = tableTemplate.content.cloneNode(true);
    const tbody = tableClone.querySelector('tbody');

    for (const comic of comics) {
        try {
            const rowClone = rowTemplate.content.cloneNode(true);
            _fillRow(rowClone, comic, callbacks);
            tbody.appendChild(rowClone);
        } catch (err) {
            console.error("Impossible d'afficher la BD ID : " + comic.id, err);
        }
    }

    comicList.appendChild(tableClone);
}

/**
 * Calcule et met à jour les indicateurs statistiques dans le pied de page de l'application.
 * * @param {Object[]} comics - Le tableau complet de toutes les bandes dessinées de la base.
 */
export function renderStats(comics) {
    const statCount = document.getElementById('stat-count');
    const statSize = document.getElementById('stat-size');

    if (statCount) statCount.textContent = comics.length;

    if (statSize) {
        // Estimation du poids de la base de données en mesurant la chaîne JSON
        const jsonString = JSON.stringify(comics);
        statSize.textContent = formatBytes(new Blob([jsonString]).size);
    }
}

/**
 * Initialise le comportement responsive de la sidebar de navigation.
 * Sur desktop, la sidebar est toujours visible (CSS). Sur mobile, le bouton
 * burger l'affiche en overlay et un clic sur le contenu principal la referme.
 */
export function initSidebar() {
    const sidebar = document.getElementById('sidebar-nav');
    const btnBurger = document.getElementById('btn-burger');
    const btnCloseSidebar = document.getElementById('btn-close-sidebar');

    function openSidebar() {
        sidebar.classList.add('is-open');
        btnBurger.setAttribute('aria-expanded', 'true');
    }

    function closeSidebar() {
        sidebar.classList.remove('is-open');
        btnBurger.setAttribute('aria-expanded', 'false');
    }

    btnBurger.addEventListener('click', (e) => {
        e.stopPropagation();
        if (sidebar.classList.contains('is-open')) {
            closeSidebar();
        } else {
            openSidebar();
        }
    });

    btnCloseSidebar.addEventListener('click', closeSidebar);

    // Fermeture automatique au clic sur le contenu principal (mobile uniquement)
    document.querySelector('.main-content').addEventListener('click', () => {
        if (sidebar.classList.contains('is-open')) closeSidebar();
    });

    // Fermeture quand l'utilisateur active un lien de navigation (mobile UX)
    sidebar.querySelectorAll('.nav-link[data-filter], .nav-link[data-section]').forEach(link => {
        link.addEventListener('click', () => {
            if (sidebar.classList.contains('is-open')) closeSidebar();
        });
    });
}

/**
 * Initialise le menu déroulant (Dropdown) des options générales de l'application.
 * Gère l'accessibilité (attributs ARIA) et la fermeture intelligente au clic extérieur.
 */
export function initDropdown() {
    const btnOptions = document.getElementById('btn-options');
    const optionsMenu = document.getElementById('options-menu');

    // Bascule de l'affichage au clic sur le bouton principal
    btnOptions.addEventListener('click', (e) => {
        e.stopPropagation();
        const isHidden = optionsMenu.classList.contains('is-hidden');

        optionsMenu.classList.toggle('is-hidden', !isHidden);
        btnOptions.setAttribute('aria-expanded', String(isHidden));
    });

    // Fermeture intelligente si clic en dehors du menu ou du bouton
    document.addEventListener('click', (e) => {
        if (!optionsMenu.classList.contains('is-hidden') &&
            !optionsMenu.contains(e.target) &&
            !btnOptions.contains(e.target)) {

            optionsMenu.classList.add('is-hidden');
            btnOptions.setAttribute('aria-expanded', 'false');
        }
    });

    // Fermeture automatique du menu après avoir sélectionné une action (ex: Exporter)
    optionsMenu.addEventListener('click', () => {
        optionsMenu.classList.add('is-hidden');
        btnOptions.setAttribute('aria-expanded', 'false');
    });
}

// ==========================================
// --- FONCTIONS PRIVÉES (Helpers) ---
// ==========================================

/**
 * Remplit le clone du template HTML avec les données spécifiques d'une bande dessinée.
 * Gère dynamiquement l'affichage ou la suppression des balises optionnelles (série, éditeur...).
 * * @private
 * @param {DocumentFragment|HTMLElement} clone - Le fragment HTML de la carte en cours de construction.
 * @param {Object} comic - Les données de la bande dessinée.
 */
function _fillCard(clone, comic) {
    // Remplissage de l'image de couverture
    // Si pas de couverture : on ne sette pas src (évite l'icône cassée) et le
    // fond gris défini en CSS remplit la zone grâce à aspect-ratio: 2/3.
    const coverEl = clone.querySelector('.comic-cover');
    if (comic.cover) {
        coverEl.src = comic.cover;
        coverEl.alt = comic.title || 'Couverture non disponible';
    } else {
        coverEl.removeAttribute('src');
        coverEl.alt = '';
        coverEl.setAttribute('aria-hidden', 'true');
    }

    // Titre de la BD
    const titleEl = clone.querySelector('.comic-title');
    if (comic.series != comic.title) {
        titleEl.textContent = comic.title || 'Sans titre';
    } else {
        titleEl?.remove();
    }

    // Auteurs (gestion du format tableau ou chaîne brute)
    const authorsEl = clone.querySelector('.comic-authors');
    authorsEl.textContent = Array.isArray(comic.authors)
        ? comic.authors.join(', ')
        : (comic.authors || 'Auteurs inconnus');

    // Série et numéro de tome (supprime la balise si inexistant)
    const seriesEl = clone.querySelector('.comic-series');
    if (comic.series) {
        seriesEl.textContent = comic.volume ? `${comic.series} - Tome ${comic.volume}` : comic.series;
    } else {
        seriesEl?.remove();
    }

    // Éditeur (supprime la balise si inexistant)
    const publisherEl = clone.querySelector('.comic-publisher');
    if (comic.publisher) {
        publisherEl.textContent = `Éd. ${comic.publisher}`;
    } else {
        publisherEl?.remove();
    }

    // Code ISBN (supprime la balise si inexistant)
    const isbnEl = clone.querySelector('.comic-isbn');
    if (comic.isbn) {
        isbnEl.textContent = `ISBN : ${comic.isbn}`;
    } else {
        isbnEl?.remove();
    }

    // Mots-clés / Tags — affichage en liste <ul>/<li>
    const tagsEl = clone.querySelector('.comic-tags');
    if (tagsEl) {
        const tags = Array.isArray(comic.tags) ? comic.tags : (comic.tags ? comic.tags.split(',').map(t => t.trim()) : []);
        tagsEl.innerHTML = '';

        if (tags.length > 0 && tags[0]) {
            tags.forEach(tag => {
                if (tag) {
                    const li = document.createElement('li');
                    li.textContent = tag;
                    tagsEl.appendChild(li);
                }
            });
        }
    }

    // État de prêt — affiche la ligne d'info + bascule le libellé du bouton
    const loanEl = clone.querySelector('.comic-loan');
    const loanBtn = clone.querySelector('.btn-loan');
    const cardEl = clone.querySelector('.comic-card');
    const isLoaned = !!(comic.loan && comic.loan.borrower);

    if (isLoaned) {
        loanEl.textContent = formatLoanInfo(comic.loan);
        cardEl.classList.add('is-loaned');
        loanBtn.textContent = 'Rendue';
        loanBtn.classList.add('is-return');
        loanBtn.setAttribute('aria-label', 'Marquer cette bande dessinée comme rendue');
    } else {
        loanEl?.remove();
    }
}

/**
 * Remplit une ligne du tableau (mode 'list') avec les données d'une BD.
 * Une cellule absente de la BD reste vide (pas de suppression de `<td>` pour
 * préserver l'alignement des colonnes).
 *
 * @private
 * @param {DocumentFragment} clone - Fragment cloné depuis `#comic-row-template`.
 * @param {Object} comic - Les données de la bande dessinée.
 * @param {Object} callbacks - Fonctions de rappel (onEdit, onDelete, onToggleLoan).
 */
function _fillRow(clone, comic, { onEdit, onDelete, onToggleLoan, onDuplicate }) {
    const setCell = (selector, value) => {
        const el = clone.querySelector(selector);
        if (el) el.textContent = value ?? '';
    };

    // Série / Tome fusionnés ; on retombe sur le titre si pas de série
    const seriesParts = [];
    if (comic.series) {
        seriesParts.push(comic.series);
        if (comic.volume) seriesParts.push(`Tome ${comic.volume}`);
    }
    setCell('.col-series', seriesParts.join(' - '));

    // Titre masqué s'il est identique à la série (cohérent avec le rendu vignette)
    setCell('.col-title', comic.title && comic.title !== comic.series ? comic.title : '');

    setCell('.col-authors', Array.isArray(comic.authors) ? comic.authors.join(', ') : comic.authors);
    setCell('.col-publisher', comic.publisher);
    setCell('.col-isbn', comic.isbn);

    // Tags en liste <ul>/<li>
    const tagsCell = clone.querySelector('.col-tags');
    if (tagsCell) {
        const ul = tagsCell.querySelector('ul');
        ul.innerHTML = '';
        const tags = Array.isArray(comic.tags) ? comic.tags : (comic.tags ? comic.tags.split(',').map(t => t.trim()) : []);
        tags.forEach(tag => {
            if (tag) {
                const li = document.createElement('li');
                li.textContent = tag;
                ul.appendChild(li);
            }
        });
    }

    setCell('.col-addedat', formatDate(comic.addedAt));
    setCell('.col-loan', formatLoanInfo(comic.loan));

    // État prêté : classe sur la ligne + bouton bascule
    const isLoaned = !!(comic.loan && comic.loan.borrower);
    const loanBtn = clone.querySelector('.btn-loan');
    if (isLoaned) {
        clone.querySelector('.comic-row').classList.add('is-loaned');
        loanBtn.textContent = 'Rendue';
        loanBtn.classList.add('is-return');
        loanBtn.setAttribute('aria-label', 'Marquer cette bande dessinée comme rendue');
    }

    // Branchement des callbacks d'action
    clone.querySelector('.btn-edit').addEventListener('click', (e) => {
        e.stopPropagation();
        onEdit(comic);
    });
    clone.querySelector('.btn-duplicate').addEventListener('click', (e) => {
        e.stopPropagation();
        onDuplicate(comic);
    });
    clone.querySelector('.btn-delete').addEventListener('click', (e) => {
        e.stopPropagation();
        onDelete(comic);
    });
    loanBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        onToggleLoan(comic);
    });
}
